import {
  API_DELETE_DAY_REPORT,
  API_DELETE_DAY_REPORT_VOID_ENTRY,
  API_GET_DAY_REPORT_CASH_DEPOSIT_ENTRY,
  API_GET_DAY_REPORT_CASH_PO_ENTRY,
  API_GET_DAY_REPORT_SALES_ENTRY,
  API_GET_DAY_REPORT_SUMMARY,
  API_GET_DAY_REPORT_VOID_ENTRY,
  API_GET_DAY_REPORTS,
  API_GET_USER_DAY_REPORT_BY_STATUS,
  API_POST_APPROVE_DAY_REPORT,
  API_POST_DAY_REPORT_CASH_DEPOSIT_ENTRY,
  API_POST_DAY_REPORT_CASH_PO_ENTRY,
  API_POST_DAY_REPORT_CHANGE_DATE,
  API_POST_DAY_REPORT_REOPEN,
  API_POST_DAY_REPORT_REVIEW,
  API_POST_DAY_REPORT_SALES_ENTRY,
  API_POST_DAY_REPORT_VOID_ENTRY,
  API_POST_DELETE_DAY_REPORT,
  API_POST_SUBMIT_DAY_REPORT,
  API_POST_UPSERT_DAY_REPORT,
} from 'common/constants/apis';
import {CRASHLYTICS_API_ERROR} from 'common/constants/errors';
import UILogger, {LogRecordType, LogSeverityType} from 'common/utils/logger';
import httpClient from './config';
import {
  DayReportEntryType,
  IDayReport,
  IDayReportEntryGetResp,
  IDayReportEntryUpdateResp,
  IDayReportPagedDataResponse,
  IDayReportsResponse,
  IDayReportSummaryResponse,
  IDayReportUpsertResp,
  IDayReportVoidLineItem,
  IReopenDayReportResponse,
} from 'common/interfaces/iDayReports';
import {DEFAULT_RETRY_CONFIG} from 'common/constants';
import {AxiosRequestConfig} from 'axios';
import {IServerResponse, ServerResponse} from 'common/interfaces';
import {
  IGetShiftReportReviewResponse,
  IPostShiftReportReviewResponse,
  IShiftReportReview,
} from 'common/interfaces/ishiftReport';

export const getUserDayRportByStatus = async (
  storeId: number,
  status: string,
): Promise<IDayReportsResponse> => {
  UILogger.log(
    'DayReportService: Getting draft day report by storeId: ',
    storeId,
  );

  const url = API_GET_USER_DAY_REPORT_BY_STATUS.replace(
    '{storeId}',
    storeId.toString(),
  ).replace('{status}', status.toString());
  return httpClient
    .get(url)
    .then(res => {
      UILogger.log('DayReportService: Getting draft day report, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return {status: {success: false}} as IDayReportsResponse;
    })
    .catch(error => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_API_ERROR,
          },
          'Error in getting draft day report',
          error,
        );
      }
      UILogger.log(
        'Error in getting draft day report',
        error?.response?.status,
      );
      return {status: {success: false}} as IDayReportsResponse;
    });
};

export const getDayReportsForStore = async (
  storeId: number,
  startDate: string,
  endDate: string,
  pageNo: number,
  pageSize: number,
): Promise<IDayReportPagedDataResponse> => {
  UILogger.log(
    'DayReportService: Getting day reports:',
    storeId,
    startDate,
    endDate,
    pageNo,
    pageSize,
  );

  ///shifts/{startDate}/{endDate}/{pageNo}/{pageSize}
  const url = API_GET_DAY_REPORTS.replace('{storeId}', storeId.toString())
    .replace('{startDate}', startDate)
    .replace('{endDate}', endDate)
    .replace('{pageNo}', pageNo.toString())
    .replace('{pageSize}', pageSize.toString());
  return httpClient
    .get(url)
    .then(res => {
      UILogger.log('DayReportService: response, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return {status: {success: false}} as IDayReportPagedDataResponse;
    })
    .catch(error => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_API_ERROR,
          },
          'DayReportService: Error in getting draft day report',
          error,
        );
      }
      UILogger.log(
        'DayReportService: Error in getting draft day report',
        error?.response?.status,
      );
      return {status: {success: false}} as IDayReportPagedDataResponse;
    });
};

export function postUpdateDaySalesEntry<T>(
  reportId: number,
  type: DayReportEntryType,
  payload: T,
): Promise<IDayReportEntryUpdateResp<T> | undefined> {
  UILogger.info(
    'Sending update sales entry request',
    payload,
    ',type:',
    type,
    ',reportId:',
    reportId,
  );
  let url = undefined;
  switch (type) {
    case 'salesEntry':
      url = API_POST_DAY_REPORT_SALES_ENTRY;
      break;
    case 'cashPO':
      url = API_POST_DAY_REPORT_CASH_PO_ENTRY;
      break;
    case 'cashDeposit':
      url = API_POST_DAY_REPORT_CASH_DEPOSIT_ENTRY;
      break;
    case 'voids':
      url = API_POST_DAY_REPORT_VOID_ENTRY;
      break;
  }
  if (!url) {
    return Promise.resolve(undefined);
  }

  url = url.replace('{reportId}', reportId.toString());

  return httpClient
    .post(url, payload, DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then(res => {
      UILogger.log('UPDATE sales entry response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error upserting sales entry:::', error.response.data);
      return error.response.data;
    });
}

export function postUpdateDayVoidEntry<T>(
  reportId: number,
  payload: IDayReportVoidLineItem,
): Promise<IDayReportEntryUpdateResp<T> | undefined> {
  UILogger.info(
    'Sending update day report void entry request',
    payload,
    ',reportId:',
    reportId,
  );
  let url = API_POST_DAY_REPORT_VOID_ENTRY;
  url = url.replace('{reportId}', reportId.toString());

  return httpClient
    .post(url, payload, DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then(res => {
      UILogger.log('UPDATE void entry response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error upserting void entry:::', error.response.data);
      return error.response.data;
    });
}

export function postDeleteDayVoidEntry(
  reportId: number,
  voidId: number,
): Promise<IDayReportEntryUpdateResp<boolean> | undefined> {
  UILogger.info(
    'Sending update day report void delete request:voidId',
    voidId,
    ',reportId:',
    reportId,
  );
  let url = API_DELETE_DAY_REPORT_VOID_ENTRY;
  url = url
    .replace('{reportId}', reportId.toString())
    .replace('{voidId}', voidId.toString());

  return httpClient
    .delete(url, DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then(res => {
      UILogger.log('DELETE: void delete response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error deleting void entry:::', error.response.data);
      return error.response.data;
    });
}

export function getDayReportEntry<T>(
  reportId: number,
  type: DayReportEntryType,
): Promise<IDayReportEntryGetResp<T> | undefined> {
  UILogger.info(
    'Sending get day report entry request. reportId:',
    reportId,
    'type:',
    type,
  );
  let url = undefined;
  switch (type) {
    case 'salesEntry':
      url = API_GET_DAY_REPORT_SALES_ENTRY;
      break;
    case 'cashPO':
      url = API_GET_DAY_REPORT_CASH_PO_ENTRY;
      break;
    case 'cashDeposit':
      url = API_GET_DAY_REPORT_CASH_DEPOSIT_ENTRY;
      break;
    case 'voids':
      url = API_GET_DAY_REPORT_VOID_ENTRY;
      break;
  }
  if (!url) {
    return Promise.resolve(undefined);
  }

  url = url?.replace('{reportId}', reportId.toString());
  UILogger.info('Sending get day report entry request. url:', url);
  return httpClient
    .get(url, DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then(res => {
      UILogger.log('GET day report entry response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in getting day report entry:::', error.response.data);
      return error.response.data;
    });
}

export const postDeleteDayReport = (
  reportId: number,
): Promise<ServerResponse> => {
  UILogger.info('Sending deleter day report, id: ', reportId);
  return httpClient
    .delete(
      API_POST_DELETE_DAY_REPORT.replace('{reportId}', reportId.toString()),
    )
    .then(res => {
      UILogger.log('delete day report response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error deleting day report:::', error.response.data);
      return error.response.data;
    });
};

export const postUpsertDayReport = (
  dayReport: IDayReport,
  confirmed: 'confirmed' | 'validate' = 'confirmed',
): Promise<IDayReportUpsertResp> => {
  UILogger.info('Sending upsert day report, id: ', dayReport);
  return httpClient
    .post(
      API_POST_UPSERT_DAY_REPORT.replace('{confirmed}', confirmed),
      dayReport,
    )
    .then(res => {
      UILogger.log('upsert day report response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error upsert day report:::', error.response.data);
      return error.response.data;
    });
};

export const getDayReportById = async (
  reportId: number,
): Promise<IDayReportSummaryResponse> => {
  UILogger.log('DayReportService: Getting day report by reportId: ', reportId);

  const url = API_GET_DAY_REPORT_SUMMARY.replace(
    '{reportId}',
    reportId.toString(),
  );
  return httpClient
    .get(url)
    .then(res => {
      UILogger.log('DayReportService: day report by id:, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return {status: {success: false}} as IDayReportSummaryResponse;
    })
    .catch(error => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_API_ERROR,
          },
          'Error in getting day report by reportId',
          error,
        );
      }
      UILogger.log(
        'Error in getting draft day report',
        error?.response?.status,
      );
      return {status: {success: false}} as IDayReportSummaryResponse;
    });
};

export const postSubmitDayReport = (
  reportId: number,
): Promise<IDayReportUpsertResp> => {
  UILogger.info('postSubmitDayReport: ', reportId);
  return httpClient
    .post(API_POST_SUBMIT_DAY_REPORT.replace('{reportId}', reportId.toString()))
    .then(res => {
      UILogger.log('postSubmitDayReport response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in postSubmitDayReport:::', error.response.data);
      return error.response.data;
    });
};

export const postDayReportApproval = (
  reportId: number,
): Promise<IReopenDayReportResponse> => {
  UILogger.info('postDayReportApproval: ', reportId);
  return httpClient
    .get(API_POST_APPROVE_DAY_REPORT.replace('{reportId}', reportId.toString()))
    .then(res => {
      UILogger.log('postDayReportApproval response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in postDayReportApproval:::', error.response.data);
      return error.response.data;
    });
};

export const postDayReportReview = (
  review: IShiftReportReview,
): Promise<IPostShiftReportReviewResponse> => {
  UILogger.info('postDayReportReview: ', review);
  return httpClient
    .post(
      API_POST_DAY_REPORT_REVIEW.replace(
        '{reportId}',
        review.reportId.toString(),
      ),
      review,
    )
    .then(res => {
      UILogger.log('postDayReportReview response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in postDayReportReview:::', error.response.data);
      return error.response.data;
    });
};

export const getDayReportReviews = (
  reportId: number,
): Promise<IGetShiftReportReviewResponse> => {
  UILogger.info('getDayReportReviews: ', reportId);
  return httpClient
    .get(API_POST_DAY_REPORT_REVIEW.replace('{reportId}', reportId.toString()))
    .then(res => {
      UILogger.log('getDayReportReviews response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in getDayReportReviews:::', error.response.data);
      return error.response.data;
    });
};

export const postReopenDayReport = (
  reportId: number,
): Promise<IReopenDayReportResponse> => {
  UILogger.info('postReopenDayReport: ', reportId);
  return httpClient
    .get(API_POST_DAY_REPORT_REOPEN.replace('{reportId}', reportId.toString()))
    .then(res => {
      UILogger.log('postReopenDayReport response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in postReopenDayReport:::', error.response.data);
      return error.response.data;
    });
};

export const postChangeDayReportDate = (
  reportId: number,
  newReportDate: string,
): Promise<IDayReportSummaryResponse> => {
  UILogger.info('postChangeDayReportDate: ', reportId);
  return httpClient
    .post(
      API_POST_DAY_REPORT_CHANGE_DATE.replace(
        '{reportId}',
        reportId.toString(),
      ).replace('{newReportDate}', newReportDate),
    )
    .then(res => {
      UILogger.log('postChangeDayReportDate response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in postChangeDayReportDate:::', error.response.data);
      return error.response.data;
    });
};

export const postDeleteUserDayReport = (
  reportId: number,
): Promise<IServerResponse<string>> => {
  UILogger.info('postDeleteUserDayReport: ', reportId);
  return httpClient
    .delete(API_DELETE_DAY_REPORT.replace('{reportId}', reportId.toString()))
    .then(res => {
      UILogger.log('postDeleteUserDayReport response: ', res.data);
      return res.data;
    })
    .catch(error => {
      console.log('Error in postDeleteUserDayReport:::', error.response.data);
      return error.response.data;
    });
};
